import React, { useState, useEffect, useRef } from "react";
import { withStyles } from '@material-ui/core';
import * as Constants from '../../Pages/Constants';
import { FormattedMessage, useIntl } from 'react-intl';
import { Row, Col } from 'antd';

import PatientFunctionsGetter from "../../Controller/getSQLPatientFunctions.js";

const isRPPS = (value) => {
  return /^\d{9,11}$/.test(value.trim());
}

const PAGE_SIZE = 10;

/**
 * AddPractitionerPopup
 *
 * Props:
 *   cancelPopup()                — close handler
 *   onSelectPractitioner(p)      — called when a practitioner is chosen
 *   restrictToStructure {string} — when set, hide RPPS/manual and show only
 *                                  practitioners already linked to this structure.
 *                                  Pass the structure id (FK_structure of the caller).
 *   attributeToStructureId {string} — when set (admin users), show a third
 *                                  "Platform" tab listing all DB practitioners;
 *                                  selecting one auto-attributes them to this
 *                                  structure before forwarding to onSelectPractitioner.
 */
const AddPractitionerPopup = (props) => {
  const intl = useIntl();

  const restrictToStructure    = props.restrictToStructure    || null;
  const attributeToStructureId = props.attributeToStructureId || null;

  // mode: 'search' | 'manual' | 'platform'  (only relevant when !restrictToStructure)
  const [mode, setMode] = useState("search");

  // --- search mode state (RPPS / ANS) ---
  const [searchValue, setSearchValue] = useState("");
  const [givenName, setGivenName] = useState("");
  const [practitionerList, setPractitionerList] = useState([]);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [showGivenFilter, setShowGivenFilter] = useState(false);
  const [lastFamily, setLastFamily] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  // --- manual mode state ---
  const [manualRpps, setManualRpps]       = useState("");
  const [manualFamily, setManualFamily]   = useState("");
  const [manualGiven, setManualGiven]     = useState("");
  const [manualPhone, setManualPhone]     = useState("");
  const [manualMssante, setManualMssante] = useState("");
  const [manualError, setManualError]     = useState("");
  const [manualSaving, setManualSaving]   = useState(false);

  // --- structure-list mode state ---
  const [structQuery, setStructQuery]   = useState("");
  const [structList, setStructList]     = useState([]);
  const [structLoading, setStructLoading] = useState(false);
  const [structLoaded, setStructLoaded]   = useState(false);
  const [structError, setStructError]     = useState("");

  // --- platform practitioners tab state (admin only) ---
  const [platQuery, setPlatQuery]     = useState("");
  const [platList, setPlatList]       = useState([]);
  const [platLoading, setPlatLoading] = useState(false);
  const [platLoaded, setPlatLoaded]   = useState(false);
  const [platError, setPlatError]     = useState("");
  const [platAttributing, setPlatAttributing] = useState(null); // id_user being attributed
  const [platJustAdded, setPlatJustAdded]     = useState(new Set());
  const [platSendingCredRpps, setPlatSendingCredRpps] = useState(null); // rpps being emailed
  const [platCredSentRpps, setPlatCredSentRpps]       = useState(new Set()); // rpps with email sent

  const isMounted = useRef(true);
  useEffect(() => {
    isMounted.current = true;
    return () => { isMounted.current = false; };
  }, []);

  // Auto-load the structure list when the popup opens in restricted mode
  useEffect(() => {
    if (restrictToStructure && !structLoaded) {
      loadStructurePractitioners("");
    }
  }, [restrictToStructure]);

  const cancelPopup = () => { props.cancelPopup(); }

  // ---------- structure list helpers ----------
  const loadStructurePractitioners = async (q) => {
    setStructLoading(true);
    setStructError("");
    try {
      const results = await PatientFunctionsGetter.list_practitioners_by_structure(restrictToStructure, q);
      if (!isMounted.current) return;
      setStructList(Array.isArray(results) ? results : []);
    } catch(e) {
      if (!isMounted.current) return;
      setStructError("error");
    }
    if (!isMounted.current) return;
    setStructLoading(false);
    setStructLoaded(true);
  }

  const handleStructKeyPress = (e) => {
    if (e.key === 'Enter') loadStructurePractitioners(structQuery.trim());
  }

  // ---------- platform tab helpers (admin) ----------
  const loadPlatformPractitioners = async (q) => {
    setPlatLoading(true);
    setPlatError("");
    try {
      // Only practitioners made available to this structure (NULL availability = all).
      const results = await PatientFunctionsGetter.list_existing_practitioners(q, attributeToStructureId);
      if (!isMounted.current) return;
      setPlatList(Array.isArray(results) ? results : []);
    } catch(e) {
      if (!isMounted.current) return;
      setPlatError("error");
    }
    if (!isMounted.current) return;
    setPlatLoading(false);
    setPlatLoaded(true);
  }

  const handlePlatKeyPress = (e) => {
    if (e.key === 'Enter') loadPlatformPractitioners(platQuery.trim());
  }

  const handlePlatformSelect = async (p) => {
    if (!p.id_user || !attributeToStructureId) return;
    setPlatAttributing(p.id_user);
    try {
      await PatientFunctionsGetter.attribute_practitioner_to_structure(p.id_user, attributeToStructureId);
      setPlatJustAdded(prev => new Set(prev).add(p.id_user));
      props.onSelectPractitioner(p);
    } catch(e) {
      console.log(e);
    }
    setPlatAttributing(null);
  }

  const handleSendCredentials = async (p) => {
    if (!attributeToStructureId) return;
    setPlatSendingCredRpps(p.rpps);
    try {
      const result = await PatientFunctionsGetter.send_practitioner_credentials({
        id_user:       p.id_user   || undefined,
        rpps:          p.rpps,
        family:        p.family,
        given:         Array.isArray(p.given) ? p.given.join(" ") : p.given,
        profession:    p.profession,
        mssante_email: p.msmail,
        FK_structure:  attributeToStructureId,
        language:      intl.locale || 'fr-FR',
      });
      if (result?.ok) {
        setPlatCredSentRpps(prev => new Set(prev).add(p.rpps));
        // If a new id_user was created for this practitioner, update the local list
        if (!p.id_user && result.id_user) {
          setPlatList(prev => prev.map(x => x.rpps === p.rpps ? { ...x, id_user: result.id_user } : x));
        }
      }
    } catch(e) {
      console.log(e);
    }
    setPlatSendingCredRpps(null);
  }

  // ---------- search helpers (ANS RPPS) ----------
  const searchPractitioner = async () => {
    let value = searchValue.trim();
    if (!value) return;
    setLoading(true);
    setMessage("");
    setPractitionerList([]);
    setShowGivenFilter(false);
    setGivenName("");
    setLastFamily("");
    setCurrentPage(1);

    try {
      let params;
      if (isRPPS(value)) {
        params = { rpps: value };
      } else {
        params = { name: value };
      }
      let results = await PatientFunctionsGetter.search_practitioners(params);
      if (!isMounted.current) return;
      if (results.length === 0) {
        setMessage(<FormattedMessage id="practitioner.search.noResult" defaultMessage="No practitioner found" />);
      } else if (results.length > PAGE_SIZE && !isRPPS(value)) {
        setShowGivenFilter(true);
        setLastFamily(value);
      }
      setPractitionerList(results);
    } catch(e) {
      if (!isMounted.current) return;
      setMessage(<FormattedMessage id="practitioner.search.error" defaultMessage="Error during search" />);
    }
    if (!isMounted.current) return;
    setLoading(false);
  }

  const refineByGiven = async () => {
    let given = givenName.trim();
    if (!given || !lastFamily) return;
    setLoading(true);
    setMessage("");
    setPractitionerList([]);
    setCurrentPage(1);

    try {
      let results = await PatientFunctionsGetter.search_practitioners({ family: lastFamily, given: given });
      if (!isMounted.current) return;
      if (results.length === 0) {
        setMessage(<FormattedMessage id="practitioner.search.noResult" defaultMessage="No practitioner found" />);
      }
      setPractitionerList(results);
      if (results.length <= PAGE_SIZE) { setShowGivenFilter(false); }
    } catch(e) {
      if (!isMounted.current) return;
      setMessage(<FormattedMessage id="practitioner.search.error" defaultMessage="Error during search" />);
    }
    if (!isMounted.current) return;
    setLoading(false);
  }

  const selectPractitioner = (practitioner) => { props.onSelectPractitioner(practitioner); }

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && searchValue.trim()) { searchPractitioner(); }
  }

  const handleGivenKeyPress = (e) => {
    if (e.key === 'Enter' && givenName.trim()) { refineByGiven(); }
  }

  // ---------- manual helpers ----------
  const handleManualSubmit = async () => {
    setManualError("");
    const rpps = manualRpps.trim();
    const family = manualFamily.trim();
    const given = manualGiven.trim();
    if (!rpps || !family || !given) {
      setManualError(intl.formatMessage({ id: "practitioner.manual.requiredFields", defaultMessage: "Nom, Prénom and RPPS are required" }));
      return;
    }
    if (!isRPPS(rpps)) {
      setManualError(intl.formatMessage({ id: "practitioner.manual.invalidRpps", defaultMessage: "RPPS must contain 9 to 11 digits" }));
      return;
    }
    if (manualMssante.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(manualMssante.trim())) {
      setManualError(intl.formatMessage({ id: "practitioner.manual.invalidEmail", defaultMessage: "L'adresse email n'est pas valide" }));
      return;
    }
    setManualSaving(true);
    try {
      const result = await PatientFunctionsGetter.create_or_update_local_practitioner({
        rpps,
        family,
        given,
        phone: manualPhone.trim() || null,
        mssante_email: manualMssante.trim() || null
      });
      if (!isMounted.current) return;
      if (result) {
        props.onSelectPractitioner(result);
      } else {
        setManualError(intl.formatMessage({ id: "practitioner.manual.saveError", defaultMessage: "Error saving practitioner" }));
      }
    } catch(e) {
      if (!isMounted.current) return;
      setManualError(intl.formatMessage({ id: "practitioner.manual.saveError", defaultMessage: "Error saving practitioner" }));
    }
    if (!isMounted.current) return;
    setManualSaving(false);
  }

  const handleManualKeyPress = (e) => {
    if (e.key === 'Enter') { handleManualSubmit(); }
  }

  const { classes } = props;
  const inputStyle = { width: "100%", border: "1px solid gray", padding: "5px", marginTop: 3, borderRadius: 3 };
  const labelStyle = { fontWeight: "600", fontSize: "0.9em", marginBottom: 2 };

  return (
    <div className={classes.popup_background}>
      <style>
        {`
          .practitioner-popup-input::placeholder {
            font-size: 0.7em;
            color: #a8b0b8;
            font-weight: 300;
          }
        `}
      </style>
      <div style={{ height: "105%", display: "flex", flexDirection: "column", justifyContent: "center" }}>
        <div className="submit-form" style={{ height: "80%", overflowY: "auto" }}>

          {/* Title */}
          <Row className="justify-content-md-center" style={{ paddingBottom: "10px" }}>
            <Col style={{ fontWeight: "bold", fontSize: "2.2em" }}>
              <FormattedMessage id="practitioner.search.title" defaultMessage="Search practitioner" />
            </Col>
          </Row>

          {/* ===== RESTRICTED MODE: structure list only ===== */}
          {restrictToStructure ? (
            <>
              <div style={{ color: "#555", fontSize: "0.9em", marginBottom: 12, textAlign: "center" }}>
                <FormattedMessage
                  id="practitioner.structure.hint"
                  defaultMessage="Select a practitioner linked to your structure."
                />
              </div>
              <Row style={{ marginBottom: 10 }}>
                <Col style={{ display: "flex", gap: 6, width: "100%" }}>
                  <input
                    type="text"
                    className="practitioner-popup-input"
                    value={structQuery}
                    onChange={(e) => setStructQuery(e.target.value)}
                    onKeyPress={handleStructKeyPress}
                    placeholder={intl.formatMessage({ id: "practitioner.attribute.placeholder", defaultMessage: "Name, RPPS or email" })}
                    style={{ flex: 1, border: "1px solid gray", padding: "5px", borderRadius: 3 }}
                  />
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() => loadStructurePractitioners(structQuery.trim())}
                    disabled={structLoading}
                  >
                    {structLoading
                      ? <FormattedMessage id="practitioner.search.loading" defaultMessage="Searching..." />
                      : <FormattedMessage id="button.search" defaultMessage="Search" />}
                  </button>
                </Col>
              </Row>

              {structLoading && (
                <div style={{ color: "gray", textAlign: "center" }}>
                  <FormattedMessage id="practitioner.search.loading" defaultMessage="Searching..." />
                </div>
              )}
              {structError && (
                <div style={{ color: "red", textAlign: "center", fontSize: "0.9em" }}>
                  <FormattedMessage id="practitioner.search.error" defaultMessage="Error during search" />
                </div>
              )}
              {structLoaded && !structLoading && !structError && structList.length === 0 && (
                <div style={{ color: "gray", textAlign: "center" }}>
                  <FormattedMessage id="practitioner.attribute.noResult" defaultMessage="No existing practitioner found." />
                </div>
              )}

              {structList.length > 0 && (() => {
                const linked    = structList.filter(p => p.source !== 'available');
                const available = structList.filter(p => p.source === 'available');
                const renderCard = (p, idx) => (
                  <div
                    key={p.id_practitioner || idx}
                    onClick={() => selectPractitioner(p)}
                    style={{
                      border: "1px solid #ccc", borderRadius: 4, padding: 10, marginBottom: 5,
                      cursor: "pointer", backgroundColor: "#f9f9f9",
                      transition: "background-color 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease"
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = "#e6f3ff";
                      e.currentTarget.style.borderColor = "#4da3ff";
                      e.currentTarget.style.boxShadow = "0 0 0 2px rgba(77,163,255,0.2)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "#f9f9f9";
                      e.currentTarget.style.borderColor = "#ccc";
                      e.currentTarget.style.boxShadow = "none";
                    }}
                  >
                    <b>{p.family}{p.given && p.given.length > 0 ? `, ${p.given.join(" ")}` : ""}</b><br />
                    {p.profession && <span>{p.profession}</span>}
                    {p.rpps && <span> — RPPS: {p.rpps}</span>}<br />
                    {(p.msmail || p.mssante_email) && <span style={{ fontSize: "0.85em", color: "#555" }}>{p.msmail || p.mssante_email}</span>}
                    {p.current_structure_name && (
                      <div style={{ fontSize: "0.8em", color: "#777", marginTop: 2 }}>
                        <FormattedMessage id="account.structure" defaultMessage="Structure" />: {p.current_structure_name}
                      </div>
                    )}
                  </div>
                );
                return (
                  <div>
                    {linked.length > 0 && (
                      <>
                        <div style={{ marginBottom: 5, fontWeight: "bold" }}>
                          <FormattedMessage id="practitioner.search.resultsCount" defaultMessage="{count} result(s) found" values={{ count: linked.length }} />
                        </div>
                        {linked.map((p, idx) => renderCard(p, idx))}
                      </>
                    )}
                    {available.length > 0 && (
                      <>
                        <div style={{ margin: "12px 0 6px", fontWeight: "bold", color: "#555", borderTop: "1px solid #e0e0e0", paddingTop: 10 }}>
                          <FormattedMessage id="practitioner.available.title" defaultMessage="Available practitioners" />
                          {" "}
                          <span style={{ fontWeight: 400, fontSize: "0.85em" }}>({available.length})</span>
                        </div>
                        {available.map((p, idx) => renderCard(p, idx))}
                      </>
                    )}
                  </div>
                );
              })()}
            </>
          ) : (
            <>
              {/* Mode tabs (only in unrestricted mode) */}
              <Row style={{ marginBottom: 15 }}>
                <Col style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <button
                    className={`btn btn-sm ${mode === 'search' ? 'btn-primary' : 'btn-outline-secondary'}`}
                    onClick={() => { setMode('search'); setManualError(''); }}
                  >
                    <FormattedMessage id="practitioner.mode.search" defaultMessage="Search RPPS database" />
                  </button>
                  <button
                    className={`btn btn-sm ${mode === 'manual' ? 'btn-warning' : 'btn-outline-secondary'}`}
                    onClick={() => { setMode('manual'); setMessage(''); setPractitionerList([]); }}
                  >
                    <FormattedMessage id="practitioner.mode.manual" defaultMessage="Enter manually" />
                  </button>
                  {attributeToStructureId && (
                    <button
                      className={`btn btn-sm ${mode === 'platform' ? 'btn-info' : 'btn-outline-secondary'}`}
                      onClick={() => {
                        setMode('platform');
                        setManualError('');
                        if (!platLoaded) loadPlatformPractitioners("");
                      }}
                    >
                      <FormattedMessage id="practitioner.mode.platform" defaultMessage="Platform practitioners" />
                    </button>
                  )}
                </Col>
              </Row>

              {/* ===== SEARCH MODE ===== */}
              {mode === 'search' && (
                <>
                  <Row style={{ width: "100%", fontSize: "120%", marginBottom: 10 }}>
                    <Col>
                      <label htmlFor="practitioner_search">
                        <FormattedMessage id="practitioner.search.label" defaultMessage="Lastname or RPPS number:" />
                      </label>
                      <input
                        type="text"
                        id="practitioner_search"
                        required
                        value={searchValue}
                        onChange={(e) => setSearchValue(e.target.value)}
                        onKeyPress={handleKeyPress}
                        className="practitioner-popup-input"
                        placeholder={intl.formatMessage({
                          id: "practitioner.search.placeholder",
                          defaultMessage: "Name or RPPS number..."
                        })}
                        style={{ width: "100%", border: "1px solid gray", padding: "5px" }}
                      />
                    </Col>
                  </Row>

                  {showGivenFilter && (
                    <Row style={{ width: "100%", fontSize: "120%", marginBottom: 10 }}>
                      <Col>
                        <label htmlFor="practitioner_given">
                          <FormattedMessage id="practitioner.search.givenLabel" defaultMessage="Too many results. Enter first name to refine:" />
                        </label>
                        <div style={{ display: "flex", gap: 5 }}>
                          <input
                            type="text"
                            id="practitioner_given"
                            value={givenName}
                            onChange={(e) => setGivenName(e.target.value)}
                            onKeyPress={handleGivenKeyPress}
                            className="practitioner-popup-input"
                            placeholder={intl.formatMessage({
                              id: "practitioner.search.givenPlaceholder",
                              defaultMessage: "First name..."
                            })}
                            style={{ flex: 1, border: "1px solid gray", padding: "5px" }}
                          />
                          <button onClick={refineByGiven} className="btn btn-info btn-sm" disabled={!givenName.trim() || loading}>
                            <FormattedMessage id="practitioner.search.refine" defaultMessage="Refine" />
                          </button>
                        </div>
                      </Col>
                    </Row>
                  )}

                  {practitionerList.length > 0 && (
                    <Row style={{ width: "100%", fontSize: "100%", marginBottom: 10 }}>
                      <Col style={{ width: "100%" }}>
                        <div style={{ marginBottom: 5, fontWeight: "bold" }}>
                          <FormattedMessage id="practitioner.search.resultsCount" defaultMessage="{count} result(s) found" values={{ count: practitionerList.length }} />
                        </div>
                        {practitionerList.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE).map((practitioner, index) => (
                          <div
                            key={index}
                            onClick={() => selectPractitioner(practitioner)}
                            style={{
                              border: "1px solid #ccc", borderRadius: 4, padding: 10, marginBottom: 5,
                              cursor: "pointer", backgroundColor: "#f9f9f9",
                              transition: "background-color 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease"
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = "#e6f3ff"
                              e.currentTarget.style.borderColor = "#4da3ff"
                              e.currentTarget.style.boxShadow = "0 0 0 2px rgba(77, 163, 255, 0.2)"
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = "#f9f9f9"
                              e.currentTarget.style.borderColor = "#ccc"
                              e.currentTarget.style.boxShadow = "none"
                            }}
                          >
                            <b>{practitioner.family} {practitioner.given ? practitioner.given.join(" ") : ""}</b><br />
                            {practitioner.profession && <span>{practitioner.profession}</span>}
                            {practitioner.rpps && <span> - RPPS: {practitioner.rpps}</span>}<br />
                            {(practitioner.msmail || practitioner.mssante_email) && (
                              <span>{practitioner.msmail || practitioner.mssante_email}</span>
                            )}
                            {practitioner.current_structure_name && (
                              <div style={{ fontSize: "0.85em", color: "#555", marginTop: 2 }}>
                                <FormattedMessage id="account.structure" defaultMessage="Structure" />: {practitioner.current_structure_name}
                              </div>
                            )}
                          </div>
                        ))}
                        {practitionerList.length > PAGE_SIZE && (
                          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 10, marginTop: 8 }}>
                            <button
                              className="btn btn-outline-secondary btn-sm"
                              disabled={currentPage === 1}
                              onClick={() => setCurrentPage(p => p - 1)}
                            >
                              <FormattedMessage id="practitioner.search.prevPage" defaultMessage="Previous" />
                            </button>
                            <span style={{ fontSize: "0.9em" }}>
                              <FormattedMessage
                                id="practitioner.search.pageInfo"
                                defaultMessage="Page {page} / {totalPages}"
                                values={{ page: currentPage, totalPages: Math.ceil(practitionerList.length / PAGE_SIZE) }}
                              />
                            </span>
                            <button
                              className="btn btn-outline-secondary btn-sm"
                              disabled={currentPage >= Math.ceil(practitionerList.length / PAGE_SIZE)}
                              onClick={() => setCurrentPage(p => p + 1)}
                            >
                              <FormattedMessage id="practitioner.search.nextPage" defaultMessage="Next" />
                            </button>
                          </div>
                        )}
                      </Col>
                    </Row>
                  )}

                  {message && (
                    <Row style={{ width: "90%", fontSize: "120%", margin: "auto" }}>
                      <h4>{message}</h4>
                    </Row>
                  )}

                  <br />
                  <div style={{ textAlign: "center" }}>
                    <button onClick={searchPractitioner} className="btn btn-success" disabled={!searchValue.trim() || loading}>
                      {loading
                        ? <FormattedMessage id="practitioner.search.loading" defaultMessage="Searching..." />
                        : <FormattedMessage id="button.search" defaultMessage="Search" />
                      }
                    </button>
                  </div>
                </>
              )}

              {/* ===== MANUAL MODE ===== */}
              {mode === 'manual' && (
                <div style={{ padding: "0 4px" }}>
                  <div style={{ background: "#fff8e1", border: "1px solid #ffe082", borderRadius: 6, padding: "8px 12px", marginBottom: 14, fontSize: "0.88em", color: "#795548" }}>
                    <FormattedMessage
                      id="practitioner.manual.hint"
                      defaultMessage="Use this form when the RPPS database is unavailable. RPPS, last name and first name are required."
                    />
                  </div>

                  {/* RPPS (required) */}
                  <div style={{ marginBottom: 10 }}>
                    <label style={labelStyle}>
                      <FormattedMessage id="account.RPPS_num" defaultMessage="RPPS number" />
                      <span style={{ color: "#dc3545", marginLeft: 3 }}>*</span>
                    </label>
                    <input
                      type="text"
                      value={manualRpps}
                      onChange={(e) => setManualRpps(e.target.value)}
                      onKeyPress={handleManualKeyPress}
                      placeholder="Ex: 10004088646"
                      style={inputStyle}
                      maxLength={11}
                    />
                  </div>

                  {/* Nom (required) */}
                  <div style={{ marginBottom: 10 }}>
                    <label style={labelStyle}>
                      <FormattedMessage id="account.lastname" defaultMessage="Lastname" />
                      <span style={{ color: "#dc3545", marginLeft: 3 }}>*</span>
                    </label>
                    <input
                      type="text"
                      value={manualFamily}
                      onChange={(e) => setManualFamily(e.target.value)}
                      onKeyPress={handleManualKeyPress}
                      placeholder="Ex: DUCHEIN"
                      style={inputStyle}
                    />
                  </div>

                  {/* Prénom (required) */}
                  <div style={{ marginBottom: 10 }}>
                    <label style={labelStyle}>
                      <FormattedMessage id="account.firstname" defaultMessage="First name" />
                      <span style={{ color: "#dc3545", marginLeft: 3 }}>*</span>
                    </label>
                    <input
                      type="text"
                      value={manualGiven}
                      onChange={(e) => setManualGiven(e.target.value)}
                      onKeyPress={handleManualKeyPress}
                      placeholder="Ex: SANDRINE"
                      style={inputStyle}
                    />
                  </div>

                  {/* Téléphone (optional) */}
                  <div style={{ marginBottom: 10 }}>
                    <label style={labelStyle}>
                      <FormattedMessage id="account.phone" defaultMessage="Phone" />
                    </label>
                    <input
                      type="tel"
                      value={manualPhone}
                      onChange={(e) => setManualPhone(e.target.value)}
                      onKeyPress={handleManualKeyPress}
                      placeholder="Ex: 0612345678"
                      style={inputStyle}
                    />
                  </div>

                  {/* Adresse MSSanté ou email (optional) */}
                  <div style={{ marginBottom: 14 }}>
                    <label style={labelStyle}>
                      <FormattedMessage id="practitioner.manual.mssanteOrEmail" defaultMessage="Adresse MSSanté ou email" />
                    </label>
                    <input
                      type="email"
                      value={manualMssante}
                      onChange={(e) => { setManualMssante(e.target.value); setManualError(""); }}
                      onKeyPress={handleManualKeyPress}
                      placeholder="Ex: prenom.nom@medecin.mssante.fr"
                      style={{
                        ...inputStyle,
                        borderColor: manualMssante.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(manualMssante.trim()) ? '#dc3545' : undefined
                      }}
                    />
                    {manualMssante.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(manualMssante.trim()) && (
                      <div style={{ color: '#dc3545', fontSize: '0.8em', marginTop: 3 }}>
                        <FormattedMessage id="practitioner.manual.invalidEmail" defaultMessage="L'adresse email n'est pas valide" />
                      </div>
                    )}
                  </div>

                  {manualError && (
                    <div style={{ color: "#dc3545", marginBottom: 8, fontSize: "0.9em" }}>{manualError}</div>
                  )}

                  <div style={{ textAlign: "center" }}>
                    <button
                      className="btn btn-warning"
                      onClick={handleManualSubmit}
                      disabled={manualSaving || !manualRpps.trim() || !manualFamily.trim() || !manualGiven.trim() || (manualMssante.trim() !== '' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(manualMssante.trim()))}
                    >
                      {manualSaving
                        ? <FormattedMessage id="button.saving" defaultMessage="Saving..." />
                        : <FormattedMessage id="button.save" defaultMessage="Save" />
                      }
                    </button>
                  </div>
                </div>
              )}

              {/* ===== PLATFORM MODE ===== */}
              {mode === 'platform' && (
                <div>
                  <div style={{ color: "#555", fontSize: "0.9em", marginBottom: 10 }}>
                    <FormattedMessage
                      id="practitioner.platform.hint"
                      defaultMessage="Search practitioners already registered in the platform. Selecting one will automatically link them to this structure."
                    />
                  </div>
                  <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
                    <input
                      type="text"
                      className="practitioner-popup-input"
                      value={platQuery}
                      onChange={(e) => setPlatQuery(e.target.value)}
                      onKeyPress={handlePlatKeyPress}
                      placeholder={intl.formatMessage({ id: "practitioner.attribute.placeholder", defaultMessage: "Name, RPPS or email" })}
                      style={{ flex: 1, border: "1px solid gray", padding: "5px", borderRadius: 3 }}
                    />
                    <button
                      className="btn btn-info btn-sm"
                      onClick={() => loadPlatformPractitioners(platQuery.trim())}
                      disabled={platLoading}
                    >
                      {platLoading
                        ? <FormattedMessage id="practitioner.search.loading" defaultMessage="Searching..." />
                        : <FormattedMessage id="button.search" defaultMessage="Search" />}
                    </button>
                  </div>

                  {platLoading && (
                    <div style={{ color: "gray", textAlign: "center" }}>
                      <FormattedMessage id="practitioner.search.loading" defaultMessage="Searching..." />
                    </div>
                  )}
                  {platError && (
                    <div style={{ color: "red", textAlign: "center", fontSize: "0.9em" }}>
                      <FormattedMessage id="practitioner.search.error" defaultMessage="Error during search" />
                    </div>
                  )}
                  {platLoaded && !platLoading && !platError && platList.length === 0 && (
                    <div style={{ color: "gray", textAlign: "center" }}>
                      <FormattedMessage id="practitioner.attribute.noResult" defaultMessage="No existing practitioner found." />
                    </div>
                  )}

                  {platList.length > 0 && (
                    <div>
                      <div style={{ marginBottom: 5, fontWeight: "bold" }}>
                        <FormattedMessage id="practitioner.search.resultsCount" defaultMessage="{count} result(s) found" values={{ count: platList.length }} />
                      </div>
                      {platList.map((p, idx) => {
                        const isAdded       = platJustAdded.has(p.id_user);
                        const isAttributing = platAttributing === p.id_user;
                        const credSent      = platCredSentRpps.has(p.rpps);
                        const isSendingCred = platSendingCredRpps === p.rpps;
                        const busyRow       = !!platAttributing || !!platSendingCredRpps;
                        return (
                          <div
                            key={p.id_practitioner || idx}
                            style={{
                              border: `1px solid ${isAdded ? '#4CAF50' : '#ccc'}`,
                              borderRadius: 4, padding: "8px 12px", marginBottom: 5,
                              background: isAdded ? "#e8f5e9" : "#fafafa",
                              display: "flex", alignItems: "center", gap: 10,
                            }}
                          >
                            <div style={{ flex: 1 }}>
                              <b>{p.family}{p.given && p.given.length > 0 ? `, ${p.given.join(" ")}` : ""}</b>
                              {p.profession && <span style={{ marginLeft: 8, fontSize: "0.9em", color: "#555" }}>{p.profession}</span>}
                              {p.rpps && <div style={{ fontSize: "0.83em", color: "#777" }}>RPPS: {p.rpps}</div>}
                              {(p.msmail || p.email) && <div style={{ fontSize: "0.83em", color: "#777" }}>{p.msmail || p.email}</div>}
                              {p.current_structure_name && (
                                <div style={{ fontSize: "0.8em", color: "#999" }}>
                                  <FormattedMessage id="practitioner.attribute.currentStructure" defaultMessage="Current structure:" /> {p.current_structure_name}
                                </div>
                              )}
                            </div>
                            <div style={{ flexShrink: 0, display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 5 }}>
                              {isAdded ? (
                                <span style={{ color: "#2e7d32", fontWeight: 700, fontSize: "0.88em" }}>
                                  &#x2713; <FormattedMessage id="practitioner.attribute.added" defaultMessage="Added" />
                                </span>
                              ) : p.id_user ? (
                                <>
                                  <span style={{ fontSize: "0.78em", color: "#555", textAlign: "right" }}>
                                    <FormattedMessage id="practitioner.platform.hasAccount" defaultMessage="Ce médecin a déjà un compte" />
                                  </span>
                                  {credSent ? (
                                    <span style={{ color: "#2e7d32", fontSize: "0.84em", fontWeight: 600 }}>
                                      &#x2713; <FormattedMessage id="practitioner.platform.emailSent" defaultMessage="Email envoyé" />
                                    </span>
                                  ) : (
                                    <button
                                      className="btn btn-sm btn-outline-info"
                                      disabled={busyRow}
                                      onClick={() => handleSendCredentials(p)}
                                    >
                                      {isSendingCred
                                        ? <FormattedMessage id="home.loading" defaultMessage="Loading..." />
                                        : <FormattedMessage id="practitioner.platform.resendCredentials" defaultMessage="Renvoyer les identifiants" />}
                                    </button>
                                  )}
                                  <button
                                    className="btn btn-sm btn-success"
                                    disabled={busyRow}
                                    onClick={() => handlePlatformSelect(p)}
                                  >
                                    {isAttributing
                                      ? <FormattedMessage id="home.loading" defaultMessage="Loading..." />
                                      : <FormattedMessage id="practitioner.attribute.btn" defaultMessage="Attribute" />}
                                  </button>
                                </>
                              ) : p.msmail ? (
                                credSent ? (
                                  <span style={{ color: "#2e7d32", fontSize: "0.84em", fontWeight: 600 }}>
                                    &#x2713; <FormattedMessage id="practitioner.platform.emailSent" defaultMessage="Email envoyé" />
                                  </span>
                                ) : (
                                  <button
                                    className="btn btn-sm btn-primary"
                                    disabled={busyRow}
                                    onClick={() => handleSendCredentials(p)}
                                  >
                                    {isSendingCred
                                      ? <FormattedMessage id="home.loading" defaultMessage="Loading..." />
                                      : <FormattedMessage id="practitioner.platform.resendCredentials" defaultMessage="Renvoyer les identifiants" />}
                                  </button>
                                )
                              ) : (
                                <span style={{ color: "#bbb", fontSize: "0.84em", textAlign: "right" }}>
                                  <FormattedMessage id="practitioner.platform.noEmail" defaultMessage="Aucune adresse email" />
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {/* Close button */}
          <div style={{ textAlign: "center", marginTop: 14 }}>
            <button onClick={cancelPopup} className="btn btn-danger">
              <FormattedMessage id="button.close" defaultMessage="Close" />
            </button>
          </div>

        </div>
      </div>
    </div>
  );
}

export default withStyles(Constants.styles)(AddPractitionerPopup);
